import { easeCubicInOut, select } from "d3";

import {
  AppletElement,
  EdgeEndpoints,
  GroupSelection,
  NODE_STROKE,
  Point,
  SvgSelection,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type BananaProofNodeKind = "root" | "connector" | "center" | "leaf";

type BananaProofNodeBase = Readonly<{
  id: string;
  kind: BananaProofNodeKind;
}>;

type BananaProofNodeFrame = BananaProofNodeBase & Point;

type BananaProofEdgeKind = "root" | "branch" | "leaf";

type BananaProofEdge = Readonly<{
  id: string;
  source: string;
  target: string;
  kind: BananaProofEdgeKind;
}>;

type BananaProofFrame = Readonly<{
  nodes: readonly BananaProofNodeFrame[];
  edges: readonly BananaProofEdge[];
  durationMs: number;
  pauseMs: number;
}>;

type BananaProofLayers = Readonly<{
  edgeLayer: GroupSelection;
  nodeLayer: GroupSelection;
  counterLayer: GroupSelection;
}>;

const BANANA_PROOF_WIDTH = 620;
const BANANA_PROOF_HEIGHT = 268;
const TRANSFER_DURATION_MS = 1100;
const FRAME_PAUSE_MS = 480;
const FINAL_PAUSE_MS = 1700;
const RESTART_PAUSE_MS = 650;
const ROOT_RADIUS = 19;
const CONNECTOR_RADIUS = 14;
const CENTER_RADIUS = 17;
const LEAF_RADIUS = 9;
const ROOT_FILL = "#f6f7f2";
const CONNECTOR_FILL = "#fff";
const CENTER_FILL = "#edf7f5";
const LEAF_FILL = "#fff7e7";
const ROOT_EDGE_STROKE = "#8d9a8d";
const BRANCH_EDGE_STROKE = "#aeb7aa";
const LEAF_EDGE_STROKE = "#c99a45";
const BANANA_ICON_FILL = "#f4cf4f";
const BANANA_ICON_STROKE = "#8a6c16";
const BANANA_ICON_PATH = "M -7 1 C -4 7 5 7 8 -1 C 4 3 -2 3 -7 1 Z";
const COUNTER_FILL = "#667166";
const COUNTER_BACKGROUND_FILL = "#f7f8f4";
const COUNTER_STROKE = "#d8ded3";

const BASE_NODES: readonly BananaProofNodeBase[] = [
  { id: "v0", kind: "root" },
  { id: "p1", kind: "connector" },
  { id: "p2", kind: "connector" },
  { id: "p3", kind: "connector" },
  { id: "p4", kind: "connector" },
  { id: "w1", kind: "center" },
  { id: "w2", kind: "center" },
  { id: "w3", kind: "center" },
  { id: "w4", kind: "center" },
  { id: "u11", kind: "leaf" },
  { id: "u12", kind: "leaf" },
  { id: "u13", kind: "leaf" },
  { id: "u21", kind: "leaf" },
  { id: "u31", kind: "leaf" },
  { id: "u32", kind: "leaf" },
  { id: "u33", kind: "leaf" },
  { id: "u34", kind: "leaf" },
  { id: "u41", kind: "leaf" },
  { id: "u42", kind: "leaf" },
];

const STRUCTURE_POINTS: Readonly<Record<string, Point>> = {
  v0: { x: 310, y: 42 },
  p1: { x: 88, y: 106 },
  p2: { x: 236, y: 106 },
  p3: { x: 384, y: 106 },
  p4: { x: 532, y: 106 },
  w1: { x: 88, y: 170 },
  w2: { x: 236, y: 170 },
  w3: { x: 384, y: 170 },
  w4: { x: 532, y: 170 },
};

const AUXILIARY_LEAF_POINTS: Readonly<Record<string, Point>> = {
  u11: { x: 20, y: 166 },
  u12: { x: 29, y: 202 },
  u13: { x: 54, y: 232 },
  u21: { x: 88, y: 244 },
  u31: { x: 122, y: 232 },
  u32: { x: 147, y: 202 },
  u33: { x: 156, y: 166 },
  u34: { x: 132, y: 138 },
};

const AFTER_FIRST_TRANSFER_LEAF_POINTS: Readonly<Record<string, Point>> = {
  u11: { x: 54, y: 220 },
  u12: { x: 88, y: 226 },
  u13: { x: 122, y: 220 },
  u21: { x: 196, y: 214 },
  u31: { x: 216, y: 230 },
  u32: { x: 236, y: 236 },
  u33: { x: 256, y: 230 },
  u34: { x: 276, y: 214 },
};

const FINAL_LEAF_POINTS: Readonly<Record<string, Point>> = {
  u11: { x: 54, y: 220 },
  u12: { x: 88, y: 226 },
  u13: { x: 122, y: 220 },
  u21: { x: 236, y: 226 },
  u31: { x: 334, y: 214 },
  u32: { x: 368, y: 226 },
  u33: { x: 400, y: 226 },
  u34: { x: 434, y: 214 },
  u41: { x: 512, y: 224 },
  u42: { x: 552, y: 224 },
};

const LEAF_OWNER_BY_STAGE: readonly Readonly<Record<string, string>>[] = [
  {
    u11: "w1",
    u12: "w1",
    u13: "w1",
    u21: "w1",
    u31: "w1",
    u32: "w1",
    u33: "w1",
    u34: "w1",
  },
  {
    u11: "w1",
    u12: "w1",
    u13: "w1",
    u21: "w2",
    u31: "w2",
    u32: "w2",
    u33: "w2",
    u34: "w2",
  },
  {
    u11: "w1",
    u12: "w1",
    u13: "w1",
    u21: "w2",
    u31: "w3",
    u32: "w3",
    u33: "w3",
    u34: "w3",
  },
  {
    u11: "w1",
    u12: "w1",
    u13: "w1",
    u21: "w2",
    u31: "w3",
    u32: "w3",
    u33: "w3",
    u34: "w3",
    u41: "w4",
    u42: "w4",
  },
];

const ENTER_PARENT_BY_NODE: Readonly<Record<string, string>> = {
  p4: "v0",
  w4: "p4",
  u41: "w4",
  u42: "w4",
};

const STRUCTURE_EDGES: readonly BananaProofEdge[] = [
  { id: "v0-p1", source: "v0", target: "p1", kind: "root" },
  { id: "v0-p2", source: "v0", target: "p2", kind: "root" },
  { id: "v0-p3", source: "v0", target: "p3", kind: "root" },
  { id: "v0-p4", source: "v0", target: "p4", kind: "root" },
  { id: "p1-w1", source: "p1", target: "w1", kind: "branch" },
  { id: "p2-w2", source: "p2", target: "w2", kind: "branch" },
  { id: "p3-w3", source: "p3", target: "w3", kind: "branch" },
  { id: "p4-w4", source: "p4", target: "w4", kind: "branch" },
];

const BANANA_PROOF_FRAMES: readonly BananaProofFrame[] = [
  buildFrame({
    visibleNodeIds: ["v0", "p1", "p2", "p3", "w1", "w2", "w3", "u11", "u12", "u13", "u21", "u31", "u32", "u33", "u34"],
    visibleStructureEdgeIds: ["v0-p1", "v0-p2", "v0-p3", "p1-w1", "p2-w2", "p3-w3"],
    leafPoints: AUXILIARY_LEAF_POINTS,
    leafOwners: LEAF_OWNER_BY_STAGE[0],
    durationMs: 0,
    pauseMs: 900,
  }),
  buildFrame({
    visibleNodeIds: ["v0", "p1", "p2", "p3", "w1", "w2", "w3", "u11", "u12", "u13", "u21", "u31", "u32", "u33", "u34"],
    visibleStructureEdgeIds: ["v0-p1", "v0-p2", "v0-p3", "p1-w1", "p2-w2", "p3-w3"],
    leafPoints: AFTER_FIRST_TRANSFER_LEAF_POINTS,
    leafOwners: LEAF_OWNER_BY_STAGE[1],
    durationMs: TRANSFER_DURATION_MS,
    pauseMs: FRAME_PAUSE_MS,
  }),
  buildFrame({
    visibleNodeIds: ["v0", "p1", "p2", "p3", "w1", "w2", "w3", "u11", "u12", "u13", "u21", "u31", "u32", "u33", "u34"],
    visibleStructureEdgeIds: ["v0-p1", "v0-p2", "v0-p3", "p1-w1", "p2-w2", "p3-w3"],
    leafPoints: FINAL_LEAF_POINTS,
    leafOwners: LEAF_OWNER_BY_STAGE[2],
    durationMs: TRANSFER_DURATION_MS,
    pauseMs: FRAME_PAUSE_MS,
  }),
  buildFrame({
    visibleNodeIds: ["v0", "p1", "p2", "p3", "p4", "w1", "w2", "w3", "w4", "u11", "u12", "u13", "u21", "u31", "u32", "u33", "u34", "u41", "u42"],
    visibleStructureEdgeIds: ["v0-p1", "v0-p2", "v0-p3", "v0-p4", "p1-w1", "p2-w2", "p3-w3", "p4-w4"],
    leafPoints: FINAL_LEAF_POINTS,
    leafOwners: LEAF_OWNER_BY_STAGE[3],
    durationMs: TRANSFER_DURATION_MS,
    pauseMs: FINAL_PAUSE_MS,
  }),
];

function buildFrame(config: Readonly<{
  visibleNodeIds: readonly string[];
  visibleStructureEdgeIds: readonly string[];
  leafPoints: Readonly<Record<string, Point>>;
  leafOwners: Readonly<Record<string, string>>;
  durationMs: number;
  pauseMs: number;
}>): BananaProofFrame {
  const visibleNodeIds = new Set(config.visibleNodeIds);
  const visibleStructureEdgeIds = new Set(config.visibleStructureEdgeIds);

  const nodes = BASE_NODES.reduce<BananaProofNodeFrame[]>((frameNodes, baseNode: BananaProofNodeBase): BananaProofNodeFrame[] => {
    if (!visibleNodeIds.has(baseNode.id)) {
      return frameNodes;
    }

    const point = STRUCTURE_POINTS[baseNode.id] ?? config.leafPoints[baseNode.id];
    if (point !== undefined) {
      frameNodes.push({ ...baseNode, ...point });
    }
    return frameNodes;
  }, []);

  const structureEdges = STRUCTURE_EDGES.filter((edge: BananaProofEdge): boolean => visibleStructureEdgeIds.has(edge.id));
  const leafEdges = Object.entries(config.leafOwners).map(([leafId, ownerId]: [string, string]): BananaProofEdge => ({
    id: `leaf-${leafId}`,
    source: ownerId,
    target: leafId,
    kind: "leaf",
  }));

  return {
    nodes,
    edges: [...structureEdges, ...leafEdges],
    durationMs: config.durationMs,
    pauseMs: config.pauseMs,
  };
}

function nodeLookup(frame: BananaProofFrame): ReadonlyMap<string, BananaProofNodeFrame> {
  return new Map(frame.nodes.map((node: BananaProofNodeFrame): [string, BananaProofNodeFrame] => [node.id, node]));
}

function nodeRadius(node: BananaProofNodeBase): number {
  switch (node.kind) {
    case "root":
      return ROOT_RADIUS;
    case "connector":
      return CONNECTOR_RADIUS;
    case "center":
      return CENTER_RADIUS;
    case "leaf":
      return LEAF_RADIUS;
  }
}

function nodeFill(node: BananaProofNodeBase): string {
  switch (node.kind) {
    case "root":
      return ROOT_FILL;
    case "connector":
      return CONNECTOR_FILL;
    case "center":
      return CENTER_FILL;
    case "leaf":
      return LEAF_FILL;
  }
}

function nodeStrokeWidth(node: BananaProofNodeBase): number {
  switch (node.kind) {
    case "root":
      return 1.8;
    case "connector":
      return 1.4;
    case "center":
      return 1.7;
    case "leaf":
      return 1.3;
  }
}

function edgeStroke(edge: BananaProofEdge): string {
  switch (edge.kind) {
    case "root":
      return ROOT_EDGE_STROKE;
    case "branch":
      return BRANCH_EDGE_STROKE;
    case "leaf":
      return LEAF_EDGE_STROKE;
  }
}

function edgeStrokeWidth(edge: BananaProofEdge): number {
  switch (edge.kind) {
    case "root":
      return 2.5;
    case "branch":
      return 2.2;
    case "leaf":
      return 1.8;
  }
}

function edgeEndpoints(edge: BananaProofEdge, nodes: ReadonlyMap<string, BananaProofNodeFrame>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, nodeRadius(source), nodeRadius(target));
}

function nodeEntryPoint(node: BananaProofNodeFrame, previousFrame?: BananaProofFrame): Point {
  const parentId = ENTER_PARENT_BY_NODE[node.id];
  if (parentId === undefined) {
    return node;
  }

  const previousParent = previousFrame === undefined ? undefined : nodeLookup(previousFrame).get(parentId);
  const currentParent = STRUCTURE_POINTS[parentId];
  return previousParent ?? currentParent ?? node;
}

function appendSvg(element: AppletElement): SvgSelection {
  return select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${BANANA_PROOF_WIDTH} ${BANANA_PROOF_HEIGHT}`)
    .attr("width", BANANA_PROOF_WIDTH)
    .attr("height", BANANA_PROOF_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "Autoplaying unlabeled banana tree proof animation.");
}

function renderFrame(
  layers: BananaProofLayers,
  frame: BananaProofFrame,
  frameIndex: number,
  previousFrame?: BananaProofFrame,
): void {
  const nodes = nodeLookup(frame);
  const durationMs = frame.durationMs;
  renderCounter(layers.counterLayer, frameIndex);

  const lines = layers.edgeLayer
    .selectAll<SVGLineElement, BananaProofEdge>("line")
    .data(frame.edges, (edge: BananaProofEdge): string => edge.id);

  const lineEnter = lines.enter()
    .append<SVGLineElement>("line")
    .attr("stroke", (edge: BananaProofEdge): string => edgeStroke(edge))
    .attr("stroke-width", (edge: BananaProofEdge): number => edgeStrokeWidth(edge))
    .attr("stroke-linecap", "round")
    .attr("x1", (edge: BananaProofEdge): number => {
      const source = nodes.get(edge.source);
      return source?.x ?? 0;
    })
    .attr("y1", (edge: BananaProofEdge): number => {
      const source = nodes.get(edge.source);
      return source?.y ?? 0;
    })
    .attr("x2", (edge: BananaProofEdge): number => {
      const source = nodes.get(edge.source);
      return source?.x ?? 0;
    })
    .attr("y2", (edge: BananaProofEdge): number => {
      const source = nodes.get(edge.source);
      return source?.y ?? 0;
    });

  lineEnter.transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("x1", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).y2);

  lines.attr("stroke", (edge: BananaProofEdge): string => edgeStroke(edge))
    .attr("stroke-width", (edge: BananaProofEdge): number => edgeStrokeWidth(edge))
    .transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("x1", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: BananaProofEdge): number => edgeEndpoints(edge, nodes).y2);

  lines.exit()
    .transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("x2", function (): string | null {
      return this.getAttribute("x1");
    })
    .attr("y2", function (): string | null {
      return this.getAttribute("y1");
    })
    .remove();

  const circles = layers.nodeLayer
    .selectAll<SVGCircleElement, BananaProofNodeFrame>("circle")
    .data(frame.nodes, (node: BananaProofNodeFrame): string => node.id);

  const circleEnter = circles.enter()
    .append<SVGCircleElement>("circle")
    .attr("cx", (node: BananaProofNodeFrame): number => nodeEntryPoint(node, previousFrame).x)
    .attr("cy", (node: BananaProofNodeFrame): number => nodeEntryPoint(node, previousFrame).y)
    .attr("r", 0)
    .attr("fill", (node: BananaProofNodeFrame): string => nodeFill(node))
    .attr("stroke", NODE_STROKE)
    .attr("stroke-width", (node: BananaProofNodeFrame): number => nodeStrokeWidth(node))
    .attr("data-node-id", (node: BananaProofNodeFrame): string => node.id)
    .attr("data-node-kind", (node: BananaProofNodeFrame): BananaProofNodeKind => node.kind);

  circleEnter.transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("cx", (node: BananaProofNodeFrame): number => node.x)
    .attr("cy", (node: BananaProofNodeFrame): number => node.y)
    .attr("r", (node: BananaProofNodeFrame): number => nodeRadius(node));

  circles.attr("fill", (node: BananaProofNodeFrame): string => nodeFill(node))
    .attr("stroke-width", (node: BananaProofNodeFrame): number => nodeStrokeWidth(node))
    .transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("cx", (node: BananaProofNodeFrame): number => node.x)
    .attr("cy", (node: BananaProofNodeFrame): number => node.y)
    .attr("r", (node: BananaProofNodeFrame): number => nodeRadius(node));

  circles.exit()
    .transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("r", 0)
    .remove();

  const leafNodes = frame.nodes.filter((node: BananaProofNodeFrame): boolean => node.kind === "leaf");
  const leafIcons = layers.nodeLayer
    .selectAll<SVGPathElement, BananaProofNodeFrame>("path.banana-proof-animation-leaf-icon")
    .data(leafNodes, (node: BananaProofNodeFrame): string => node.id);

  leafIcons.enter()
    .append<SVGPathElement>("path")
    .attr("class", "banana-proof-animation-leaf-icon")
    .attr("d", BANANA_ICON_PATH)
    .attr("fill", BANANA_ICON_FILL)
    .attr("stroke", BANANA_ICON_STROKE)
    .attr("stroke-width", 0.8)
    .attr("stroke-linejoin", "round")
    .attr("pointer-events", "none")
    .attr("opacity", 0)
    .attr("transform", (node: BananaProofNodeFrame): string => bananaIconTransform(nodeEntryPoint(node, previousFrame)))
    .transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("opacity", 1)
    .attr("transform", (node: BananaProofNodeFrame): string => bananaIconTransform(node));

  leafIcons.transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("transform", (node: BananaProofNodeFrame): string => bananaIconTransform(node));

  leafIcons.exit()
    .transition()
    .duration(durationMs)
    .ease(easeCubicInOut)
    .attr("opacity", 0)
    .remove();
}

function bananaIconTransform(point: Point): string {
  return `translate(${point.x},${point.y}) rotate(-18)`;
}

function renderCounter(counterLayer: GroupSelection, frameIndex: number): void {
  counterLayer.selectAll("*").remove();

  const counter = counterLayer.append<SVGGElement>("g")
    .attr("class", "banana-proof-animation-step-counter")
    .attr("transform", `translate(${BANANA_PROOF_WIDTH - 52},12)`)
    .attr("pointer-events", "none");

  counter.append<SVGRectElement>("rect")
    .attr("width", 40)
    .attr("height", 22)
    .attr("rx", 5)
    .attr("fill", COUNTER_BACKGROUND_FILL)
    .attr("stroke", COUNTER_STROKE)
    .attr("stroke-width", 1);

  counter.append<SVGTextElement>("text")
    .attr("x", 20)
    .attr("y", 11.5)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "middle")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 12)
    .attr("font-weight", 500)
    .attr("fill", COUNTER_FILL)
    .text(`${frameIndex + 1}/${BANANA_PROOF_FRAMES.length}`);
}

function playAnimation(layers: BananaProofLayers, element: AppletElement): void {
  let frameIndex = 0;

  const playNextFrame = (): void => {
    if (!element.isConnected) {
      return;
    }

    if (frameIndex === BANANA_PROOF_FRAMES.length - 1) {
      window.setTimeout((): void => {
        frameIndex = 0;
        renderFrame(layers, BANANA_PROOF_FRAMES[0], frameIndex);
        window.setTimeout(playNextFrame, RESTART_PAUSE_MS);
      }, BANANA_PROOF_FRAMES[frameIndex].pauseMs);
      return;
    }

    const previousFrame = BANANA_PROOF_FRAMES[frameIndex];
    frameIndex += 1;
    const frame = BANANA_PROOF_FRAMES[frameIndex];
    renderFrame(layers, frame, frameIndex, previousFrame);
    window.setTimeout(playNextFrame, frame.durationMs + frame.pauseMs);
  };

  renderFrame(layers, BANANA_PROOF_FRAMES[0], frameIndex);
  window.setTimeout(playNextFrame, BANANA_PROOF_FRAMES[0].pauseMs);
}

function renderBananaProofAnimation(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const graph = appendSvg(element)
    .append<SVGGElement>("g")
    .attr("class", "banana-proof-animation-graph");

  const edgeLayer = graph.append<SVGGElement>("g")
    .attr("class", "banana-proof-animation-edges");
  const nodeLayer = graph.append<SVGGElement>("g")
    .attr("class", "banana-proof-animation-nodes");
  const counterLayer = graph.append<SVGGElement>("g")
    .attr("class", "banana-proof-animation-counter");

  playAnimation({ edgeLayer, nodeLayer, counterLayer }, element);
}

renderApplet(".banana-proof-animation-applet", renderBananaProofAnimation);
